/**
 * Zod schemas for shipping estimate API endpoint
 * Shared between client and server code
 */

import { z } from 'zod';
import { TaxEstimateSchema } from '@/schemas/api/checkout/tax-estimate';

// Zod schema for shipping estimate request (query params)
export const ShippingEstimateRequestSchema = z.object({
  listingId: z.string({ error: 'Missing required parameter: listingId' }).min(1, 'Missing required parameter: listingId'),
  addressId: z.string().optional(),
  variantVid: z.string().optional(),
});

export type ShippingEstimateRequest = z.infer<typeof ShippingEstimateRequestSchema>;

// Carrier rate schema
export const CarrierRateSchema = z.object({
  name: z.string(),
  service: z.string(),
  rate: z.number(),
  currency: z.string().optional(),
  deliveryDays: z.number().nullable().optional(),
  duration_terms: z.string().nullable().optional(),
  rateId: z.string().optional(),
  note: z.string().optional(),
});

export type CarrierRate = z.infer<typeof CarrierRateSchema>;

// Parcel dimensions schema
const ParcelSchema = z.object({
  width: z.number().nullable(),
  height: z.number().nullable(),
  depth: z.number().nullable(),
  weight: z.number().nullable(),
});

// Estimate data schema
export const EstimateDataSchema = z.object({
  fromZip: z.string(),
  toZip: z.string(),
  estimateAmount: z.number(),
  carriers: z.array(CarrierRateSchema),
  parcel: ParcelSchema,
  /**
   * Set to true for CJ listings when the supplier-partner can't fulfill the
   * requested quantity (partnerFreight returned 0 options). UI should
   * disable Buy Now and surface "Out of stock" instead of a shipping price.
   */
  isOutOfStock: z.boolean().optional(),
  /**
   * Set when the buyer and seller are in different countries. Sellwild cannot
   * buy a cross-border label, so the page shows "Arranged Separately" — the
   * same treatment vehicles get — instead of a number nobody will be charged.
   */
  arrangedSeparately: z.boolean().optional(),
  /**
   * The sales tax this listing would draw shipped to the quoted destination
   * at `estimateAmount` freight. Zero, with a reason, for the many sellers
   * who do not collect; absent only on responses from before it existed.
   */
  tax: TaxEstimateSchema.optional(),
  // CJ-listing branch returns the warehouse origin country + optional debug info.
  shipsFrom: z.string().optional(),
  defaultReason: z.string().optional(),
  _debug: z.record(z.string(), z.any()).optional(),
});

export type EstimateData = z.infer<typeof EstimateDataSchema>;

// Zod schema for shipping estimate response
export const ShippingEstimateResponseSchema = z.object({
  success: z.literal(true),
  estimate: EstimateDataSchema,
});

export type ShippingEstimateResponse = z.infer<typeof ShippingEstimateResponseSchema>;

// Error response schema
export const ShippingEstimateErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  errCode: z.string().optional(),
});

export type ShippingEstimateErrorResponse = z.infer<typeof ShippingEstimateErrorResponseSchema>;
